import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import OffersService from '~/services/OffersService';

import { offerQueryKeys } from './useGetOffer';

/**
 * Admin offer details for a set of offer ids, keyed by id. Uses the admin offer endpoint (not
 * public offers) because offline deals are not publicly visible. Shares the `useGetOffer` cache.
 * Loading is reported per offer id so each card can render as soon as its own offer resolves.
 */
export function useGetOffersByIds(offerIds: Array<string>) {
  const uniqueIds = useMemo(() => Array.from(new Set(offerIds.filter(Boolean))).sort(), [offerIds]);

  const results = useQueries({
    queries: uniqueIds.map((offerId) => ({
      queryKey: offerQueryKeys.detail(offerId),
      queryFn: async () => {
        const response = await OffersService.getOffer(offerId);
        return response.result;
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  const { offersById, loadingOfferIds } = useMemo(() => {
    const offers = new Map<string, App.Offer>();
    const loading = new Set<string>();
    results.forEach((result, index) => {
      if (result.data) {
        offers.set(uniqueIds[index], result.data);
      }
      if (result.isLoading) {
        loading.add(uniqueIds[index]);
      }
    });
    return { offersById: offers, loadingOfferIds: loading };
  }, [results, uniqueIds]);

  return { offersById, loadingOfferIds };
}
